import { useCallback, useEffect, useState } from 'react';
import { isBefore, subDays } from 'date-fns';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAdControlsFunctionality } from '@jira/growth__experiments-ad-controls/src/button/utils.tsx';
// React Sweet State is used to share state between the upsell and the sidebar without using context or prop drilling
// to keep the changes related to the experiment in one place.
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStore,
	type Action,
	type BoundActions,
	type HookFunction,
} from '@atlassian/react-sweet-state';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { UnknownError } from '@jira/polaris__lib-errors/src/UnknownError.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';

const ROADMAPS_UPSELL_FIRST_SEEN_AT_USER_PROPERTY_KEY = 'jpd-roadmaps-upsell.firstSeenAt';

const AUTO_DISMISS_AFTER_DAYS = 30;

const isFirstSeenOlderThanAutoDismissThreshold = (firstSeenAt: string): boolean =>
	isBefore(new Date(firstSeenAt), subDays(new Date(), AUTO_DISMISS_AFTER_DAYS));

const ROADMAP_UPSELL_AD_CONTROL_CONFIG = {
	userPropertyKey: 'jpd-roadmaps-upsell.dismissed',
	analytics: {
		packageName: 'jiraRoadmapsUpsell',
		teamName: 'JPD - Juno',
		panelName: 'roadmaps-upsell',
	},
	fireDefaultEvent: true,
};

export type RoadmapUpsellStoreState = {
	isDismissed: boolean;
};

const initialState: RoadmapUpsellStoreState = {
	isDismissed: false,
};

const actions = {
	setIsDismissed:
		(value: boolean): Action<RoadmapUpsellStoreState> =>
		({ setState }) => {
			setState({ isDismissed: value });
		},
};

const Store = createStore<RoadmapUpsellStoreState, typeof actions>({
	name: 'RoadmapsUpsellVisibilityStore',
	initialState,
	actions,
});

export const useRoadmapUpsellStoreHook: HookFunction<
	RoadmapUpsellStoreState,
	BoundActions<
		RoadmapUpsellStoreState,
		{
			setIsDismissed: (value: boolean) => Action<RoadmapUpsellStoreState>;
		}
	>,
	void
> = createHook(Store);

type RoadmapUpsellAdControls = {
	isDismissed: boolean;
	dismiss: () => Promise<void>;
};

export const useRoadmapUpsellAdControls = (): RoadmapUpsellAdControls => {
	const [{ isDismissed }, { setIsDismissed }] = useRoadmapUpsellStoreHook();

	const { isDismissed: localIsDismissed, dismiss: actualDismiss } = useAdControlsFunctionality(
		ROADMAP_UPSELL_AD_CONTROL_CONFIG,
	);

	useEffect(() => {
		setIsDismissed(localIsDismissed);
	}, [localIsDismissed, setIsDismissed]);

	const dismiss = useCallback(async () => {
		await actualDismiss();
		setIsDismissed(true);
	}, [actualDismiss, setIsDismissed]);

	return {
		isDismissed,
		dismiss,
	};
};

export const useRoadmapUpsellAdAutoDismissal = (): void => {
	const accountId = useAccountId();
	const [firstSeenAt, setFirstSeenAt] = useState<string | null>(null);
	const { dismiss } = useAdControlsFunctionality(ROADMAP_UPSELL_AD_CONTROL_CONFIG);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffectWithoutConcurrency(() => {
		if (!accountId || firstSeenAt !== null) {
			return;
		}

		getUserProperty<string>(accountId, ROADMAPS_UPSELL_FIRST_SEEN_AT_USER_PROPERTY_KEY)
			.then((value) => {
				setFirstSeenAt(value);
			})
			.catch((error) => {
				if (error instanceof FetchError && error.statusCode === 404) {
					const seenAt = new Date().toISOString();
					setUserProperties(
						accountId,
						ROADMAPS_UPSELL_FIRST_SEEN_AT_USER_PROPERTY_KEY,
						JSON.stringify(seenAt),
					)
						.then(() => setFirstSeenAt(seenAt))
						.catch((saveError) => {
							const unknownErrorText = `Failed to save first seen at for "${ROADMAPS_UPSELL_FIRST_SEEN_AT_USER_PROPERTY_KEY}" with account id "${accountId}"`;

							fireErrorAnalytics({
								error:
									saveError instanceof Error
										? saveError
										: new UnknownError(unknownErrorText, saveError),
								meta: {
									id: 'setUserProperties',
									packageName: 'jiraRoadmapsUpsell',
									teamName: 'JPD - Juno',
								},
								sendToPrivacyUnsafeSplunk: true,
							});

							logSafeErrorWithoutCustomerDataWrapper(
								'jiraRoadmapsUpsell.error.setFirstSeenAt',
								'Failed to save first seen at',
								saveError instanceof Error
									? saveError
									: new UnknownError(unknownErrorText, saveError),
							);
						});

					return;
				}

				if (isClientFetchError(error)) {
					return;
				}

				const unknownErrorText = `Failed to get first seen at for "${ROADMAPS_UPSELL_FIRST_SEEN_AT_USER_PROPERTY_KEY}" with account id "${accountId}"`;

				fireErrorAnalytics({
					error: new UnknownError(unknownErrorText, error),
					meta: {
						id: 'getUserProperty',
						packageName: 'jiraRoadmapsUpsell',
						teamName: 'JPD - Juno',
					},
					sendToPrivacyUnsafeSplunk: true,
				});

				logSafeErrorWithoutCustomerDataWrapper(
					'jiraRoadmapsUpsell.error.getUserProperty',
					'Failed to get first seen at',
					new UnknownError(unknownErrorText, error),
				);
			});
	}, [accountId, firstSeenAt, setFirstSeenAt]);

	useEffectWithoutConcurrency(() => {
		if (!accountId || firstSeenAt === null) {
			return;
		}

		if (!isFirstSeenOlderThanAutoDismissThreshold(firstSeenAt)) {
			return;
		}

		fireOperationalAnalytics(createAnalyticsEvent({}), 'roadmapsUpsell autoDismissed', {
			firstSeenAt,
		});
		dismiss();
	}, [accountId, firstSeenAt, dismiss, createAnalyticsEvent]);
};
